import { useEffect } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Spinner } from './components/ui';
import Setup from './pages/Setup';
import Login from './pages/Login';
import QuestHome from './pages/QuestHome';
import QuestProgress from './pages/QuestProgress';
import PetPage from './pages/PetPage';
import ParentHome from './pages/ParentHome';
import ParentChild from './pages/ParentChild';
import ParentReview from './pages/ParentReview';
import ParentFamily from './pages/ParentFamily';
import StarterChores from './pages/StarterChores';
import Profile from './pages/Profile';

/** Stroke icons on a 24px grid, so the tab bar reads as UI rather than as more emoji. */
function Icon({ d }: { d: string }): JSX.Element {
  return (
    <svg
      className="tab-icon"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const ICONS = {
  checklist: 'M9 6h11M9 12h11M9 18h11M4 6l1.2 1.2L7.5 4.8M4 12l1.2 1.2L7.5 10.8M4.5 18h1.4',
  chart: 'M4 19V5M4 19h16M8 16v-4M13 16V8M18 16v-6',
  person: 'M12 11.6a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2ZM5.4 20c0-3.5 2.9-5.7 6.6-5.7s6.6 2.2 6.6 5.7',
  paw: 'M12 13.4c2.5 0 4.6 1.8 4.6 3.7 0 1.4-1.1 2.3-2.6 2.3-.8 0-1.4-.3-2-.3s-1.2.3-2 .3c-1.5 0-2.6-.9-2.6-2.3 0-1.9 2.1-3.7 4.6-3.7ZM7.4 8.2a1.7 2.1 0 1 0 0 4.2 1.7 2.1 0 0 0 0-4.2ZM16.6 8.2a1.7 2.1 0 1 0 0 4.2 1.7 2.1 0 0 0 0-4.2ZM10.3 4.6a1.5 1.9 0 1 0 0 3.8 1.5 1.9 0 0 0 0-3.8ZM13.7 4.6a1.5 1.9 0 1 0 0 3.8 1.5 1.9 0 0 0 0-3.8Z',
  home: 'M4 10.5L12 4l8 6.5M6 9.8V20h12V9.8M10 20v-5h4v5',
  money:
    'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM12 7.5v9M14.4 9.8c-.4-.8-1.3-1.3-2.4-1.3-1.4 0-2.4.7-2.4 1.8 0 2.4 4.8 1.2 4.8 3.6 0 1.1-1 1.9-2.4 1.9-1.2 0-2.1-.5-2.5-1.4',
  sliders:
    'M4 7.5h7.5M18 7.5h2M4 16.5h2.5M13 16.5h7M16.6 7.5a2.4 2.4 0 1 1-4.8 0 2.4 2.4 0 0 1 4.8 0ZM11.6 16.5a2.4 2.4 0 1 1-4.8 0 2.4 2.4 0 0 1 4.8 0Z',
};

const KID_TABS = [
  { to: '/', icon: ICONS.checklist, label: 'Chores' },
  { to: '/pet', icon: ICONS.paw, label: 'Pet' },
  { to: '/progress', icon: ICONS.chart, label: 'Progress' },
  { to: '/me', icon: ICONS.person, label: 'Me' },
];

const PARENT_TABS = [
  { to: '/', icon: ICONS.home, label: 'Family' },
  { to: '/review', icon: ICONS.money, label: 'Money' },
  { to: '/me', icon: ICONS.sliders, label: 'Settings' },
];

export default function App(): JSX.Element {
  const { user, needsSetup, loading } = useAuth();
  const quest = user?.role === 'child';

  // The kids' theme lives on <body> so the page background and the shared
  // pieces (tab bar, inputs, cards) all pick it up.
  useEffect(() => {
    document.body.classList.toggle('quest', quest);
    return () => document.body.classList.remove('quest');
  }, [quest]);

  if (loading) return <Spinner />;
  if (needsSetup) return <Setup />;
  if (!user) return <Login />;

  const tabs = user.role === 'parent' ? PARENT_TABS : KID_TABS;

  return (
    <>
      <div className="app">
        <Routes>
          {user.role === 'parent' ? (
            <>
              <Route path="/" element={<ParentHome />} />
              <Route path="/child/:id" element={<ParentChild />} />
              <Route path="/review" element={<ParentReview />} />
              <Route path="/family" element={<ParentFamily />} />
              <Route path="/family/starter" element={<StarterChores />} />
            </>
          ) : (
            <>
              <Route path="/" element={<QuestHome />} />
              <Route path="/pet" element={<PetPage />} />
              <Route path="/progress" element={<QuestProgress />} />
            </>
          )}
          <Route path="/me" element={<Profile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <nav className="tabbar">
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.to === '/'}>
            <Icon d={tab.icon} />
            <span>{tab.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
